import { fetchAllPublisherAccountConfigs, type PublisherAccountConfigsArg } from 'src/store/fetch-all-publisher-account-configs';
import { leelaApiGenerated } from 'src/store/generated/leela-api-generated';
import type { CampaignConfigurationDto } from 'src/store/generated/leela-api-generated';

import { type PaginatedTitlesResponse } from 'src/types/titles.types';
/**
 * Campaign Configuration config value types.
 *
 * The OpenAPI code generator emits `config: object` for CampaignConfigurationDto because
 * the backend uses a discriminated union (configType + publisher) that can't be represented
 * in OpenAPI. We define the concrete types here so consumers can cast config values safely.
 *
 * When adding a new config type in the backend (campaign-configuration.dto.ts), you must
 * also add the corresponding TypeScript interface here and update CampaignConfigValue.
 */
export interface GtiBlocklistConfigValue {
  configType: 'GTI_BLOCKLIST';
  gtis: string[];
}

export interface SharedPublisherConfigValue {
  configType: 'PUBLISHER_ACCOUNT';
  timezone: string;
}

export interface MetaAccountConfigValue extends SharedPublisherConfigValue {
  publisher: 'meta';
  catalogId: string;
}

export interface TikTokAccountConfigValue extends SharedPublisherConfigValue {
  publisher: 'tiktok';
  catalogId: string;
  businessCenterId: string;
}

export interface AdspAccountConfigValue extends SharedPublisherConfigValue {
  publisher: 'adsp';
  advertiserId: string;
  userId: string;
}

export interface GoogleAccountConfigValue extends SharedPublisherConfigValue {
  publisher: 'google';
  mccAccountId: string;
  customerAccountId: string;
}

export type PublisherAccountConfigValue =
  | MetaAccountConfigValue
  | TikTokAccountConfigValue
  | AdspAccountConfigValue
  | GoogleAccountConfigValue;

export interface ContentRuleConfigValue {
  configType: 'CONTENT_RULE';
  severity: 'BLOCKER' | 'WARNING' | 'INFO';
  checks: unknown[];
  message?: string;
  description?: string;
  enabled?: boolean;
  confidenceThreshold?: number;
}

export type CampaignConfigValue =
  | GtiBlocklistConfigValue
  | PublisherAccountConfigValue
  | ContentRuleConfigValue;

/** CampaignConfigurationDto with a properly typed `config` field. */
export type TypedCampaignConfigurationDto = Omit<CampaignConfigurationDto, 'config'> & {
  config: CampaignConfigValue;
};

/**
 * IMPORTANT NOTE: any new APIs added to LeelaAPI will need to have their hooks added to the list of exports at the
 * bottom of this file in order to be used.
 *
 * 'leela-api-generated.ts' is an auto-generated file. Modifying it and regenerating it using RTK Query code generation
 * would automatically delete all the manual modifications. In order for us to be able to add additional behavior to the
 * hooks that were auto-generated (e.g. tags, reformat response etc.) we will use this file where we can enhance the
 * APIs defined in the 'leela-api-generated.ts' file.
 *
 * This file exports hooks that were created in 'leela-api-generated.ts' but with enhanced endpoints (see this link
 * https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting#enhanceendpoints for more information).
 * */

// Types for GET /distinct-values/titles
export interface GetDistinctTitlesQueryArg {
  page?: number;
  pageSize?: number;
  country?: string;
  locale?: string;
  search?: string;
  sort?: string[];
  publisher?: string;
  campaignIds?: string[];
  nextToken?: string;
}

// Types for POST /assets/by-gtis — removed, endpoint not currently used
const enhancedLeelaApi = leelaApiGenerated.enhanceEndpoints({
  addTagTypes: ['AgentSession', 'PublisherEntities'],
  endpoints: {
    agentSessionsControllerFindOne: {
      providesTags: (_result, _error, arg) => [{ type: 'AgentSession' as const, id: arg.sessionId }],
    },
    agentSessionsControllerUpdate: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'AgentSession' as const, id: arg.sessionId }],
    },
    agentSessionsControllerRemove: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'AgentSession' as const, id: arg.sessionId }],
    },
    publisherEntitiesControllerListForCampaign: {
      providesTags: ['PublisherEntities'],
    },
    programsControllerUpdate: {
      invalidatesTags: ['Assets', 'Programs'],
    },
    campaignsControllerUpdate: {
      invalidatesTags: ['Campaigns', 'Workflows'],
    },
    campaignsControllerCreate: {
      invalidatesTags: ['Campaigns', 'Workflows'],
    },
    campaignsControllerValidateNotificationRecipients: {
      invalidatesTags: [],
    },
    campaignTitlesControllerGetCampaignTitles: {
      query: (queryArg) => ({
        url: '/campaign-titles',
        method: 'POST',
        body: queryArg,
      }),
      keepUnusedDataFor: 300, // retain enrichment data across pagination
    },
    campaignConfigurationControllerDelete: {
      invalidatesTags: ['Campaign-configuration', 'Campaigns'],
    },
    campaignConfigurationControllerCreate: {
      invalidatesTags: ['Campaign-configuration'],
    },
    campaignConfigurationControllerUpdate: {
      invalidatesTags: ['Campaign-configuration'],
    },
    sonataApiControllerSearchPageIds: {
      keepUnusedDataFor: 3600, // 1 hour — page IDs are static catalog data
    },
  },
});

export const leelaApi = enhancedLeelaApi.injectEndpoints({
  endpoints: (build) => ({
    assetsControllerGetDistinctCampaignOptions: build.query<Array<{ id: string; name: string; publisher: string; timezone: string; productStrategies: unknown[]; status: string }>, void>({
      query: () => ({ url: '/distinct-values/campaigns' }),
    }),
    getDistinctTitles: build.query<PaginatedTitlesResponse, GetDistinctTitlesQueryArg>({
      query: (queryArg) => ({
        url: '/distinct-values/titles',
        params: {
          page: queryArg.page,
          pageSize: queryArg.pageSize,
          country: queryArg.country,
          locale: queryArg.locale,
          search: queryArg.search,
          sort: queryArg.sort,
          publisher: queryArg.publisher,
          campaignIds: queryArg.campaignIds,
          nextToken: queryArg.nextToken,
        },
      }),
      providesTags: (result, error, arg) => [
        { type: 'Distinct Values', id: `titles-${JSON.stringify(arg)}` },
      ],
      keepUnusedDataFor: 60, // retain across tab switches
    }),
    /**
     * Every PUBLISHER_ACCOUNT config for a publisher, fetched page by page. Shares the generated
     * list endpoint's tag so config create/update/delete invalidations refresh it too.
     */
    getAllPublisherAccountConfigs: build.query<CampaignConfigurationDto[], PublisherAccountConfigsArg>({
      queryFn: (arg, _api, _extraOptions, baseQuery) => fetchAllPublisherAccountConfigs(arg, baseQuery),
      providesTags: ['Campaign-configuration'],
    }),
    validateAlias: build.query<{ valid: boolean }, string>({
      query: (alias) => ({ url: `/users/validate/${alias}` }),
    }),
    /** Fetch all feature flags from the backend (proxies AWS AppConfig). */
    getFeatureFlags: build.query<Record<string, boolean>, void>({
      query: () => ({ url: '/feature-flags' }),
    }),
    /** Fetch SocialSignals trending recommendations. */
    getSocialSignalsRecommendations: build.query<SocialSignalsRecommendationsResponse, { page?: number; pageSize?: number; country?: string; sourcePlatform?: string; trendSearch?: string; startDate?: string; endDate?: string; topic?: string; sortBy?: string; sortDirection?: string } | void>({
      query: (args) => ({
        url: '/social-signals/recommendations',
        params: args ? { page: args.page, pageSize: args.pageSize, country: args.country, sourcePlatform: args.sourcePlatform, trendSearch: args.trendSearch, startDate: args.startDate, endDate: args.endDate, topic: args.topic, sortBy: args.sortBy, sortDirection: args.sortDirection } : undefined,
      }),
      keepUnusedDataFor: 3600,
    }),
    queryEntityMetrics: build.query<
    { data: Array<{ label?: string; date?: string; value: number }>; total: number; truncated?: boolean },
    { groupBy?: string; entity: string; timeBucket?: string; aggregation?: { op: string; field: string }; filters?: Array<{ field: string; op: string; value: string | string[] | number | boolean }>; startDate?: string; endDate?: string }
    >({
      query: (body) => ({
        url: '/metrics/entities',
        method: 'POST',
        body,
      }),
    }),
    queryPaidMedia: build.query<PaidMediaQueryResponse, PaidMediaQueryArgs>({
      query: (args) => ({
        url: '/paid-media-dashboard/query',
        method: 'POST',
        body: args,
      }),
      keepUnusedDataFor: 3600,
    }),
    queryPaidMediaInsights: build.query<PaidMediaInsightsResponse, PaidMediaInsightsArgs>({
      query: (args) => ({
        url: '/paid-media-dashboard/insights',
        method: 'POST',
        body: args,
      }),
      keepUnusedDataFor: 3600,
    }),
    /** Start an async CSV export (returns an execution id to poll). */
    startPaidMediaExport: build.mutation<ExportInitResponse, PaidMediaExportArgs>({
      query: (args) => ({
        url: '/paid-media-dashboard/export',
        method: 'POST',
        body: args,
      }),
    }),
    /** Poll a single export's status. Never cached so each poll hits the backend. */
    getPaidMediaExportStatus: build.query<ExportStatusResponse, string>({
      query: (exportId) => ({
        url: `/paid-media-dashboard/export/${exportId}`,
      }),
      keepUnusedDataFor: 0,
    }),
  }),
});

/** Response from GET /social-signals/recommendations. Maps the Andes social_signals_title_analytics table rows with pagination metadata. */
interface SocialSignalsRecommendationsResponse {
  data: Array<{
    signalId: string;
    titleId: string;
    titleName: string;
    trendText: string;
    trendSummary: string;
    llmReason: string;
    relevanceScore: number;
    promotionScore: number;
    confidenceScore: number;
    trendStrength: number;
    regionCode: string;
    datasetDate: string;
    candidateRank: number;
    matchType: string;
    entities: string;
    topics: string;
    titleGenres: string;
    matchedKeywords: string;
    signalTimestamp: string;
    riskLabel: string | null;
    sensitivityFlags: string | null;
    promotionGuidance: string | null;
    sentimentLabel: string | null;
    sentimentScore: number | null;
    sentimentSummary: string | null;
    sourcePlatforms: string | null;
    tiktokPlatformMetadata: string | null;
    serpapiPlatformMetadata: string | null;
    trendingHistory: string | null;
    volumeRaw: string | null;
    trendCategories: string | null;
    hasActiveCampaign: boolean;
  }>;
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export interface PaidMediaQueryArgs {
  view: string;
  subView: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: string;
  summary?: boolean;
  filters?: Record<string, string | string[] | undefined>;
  displayOptions?: Record<string, string>;
}

export interface PaidMediaInsightsArgs {
  view: string;
  filters?: Record<string, string | string[]>;
  displayOptions?: Record<string, string>;
  sections: Record<string, { filters?: Record<string, string | string[]> }>;
}

export interface PaidMediaInsightsSummary {
  totals: Record<string, number>;
  currency: string;
}

export interface PaidMediaInsightsTrend {
  granularity: 'day';
  points: ReadonlyArray<{
    date: string;
    spend: number;
    impressions: number;
    clicks: number;
  }>;
  currency: string;
}

export interface PaidMediaInsightsResponse {
  sections: {
    summary?: PaidMediaInsightsSummary;
    trend?: PaidMediaInsightsTrend;
  };
}

/** Request body for POST /paid-media-dashboard/export. Deliberately has no display options — CSV stays in raw local currency. */
export interface PaidMediaExportArgs {
  view: string;
  subView: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: Record<string, string | string[] | undefined>;
}

/** Response from POST /paid-media-dashboard/export — the export has been accepted and is running. */
export interface ExportInitResponse {
  exportId: string;
  status: 'running';
}

/** Response from GET /paid-media-dashboard/export/:exportId — discriminated on `status`; `url`/`expiresAt` present only when complete. */
export type ExportStatusResponse =
  | { exportId: string; status: 'running' }
  | { exportId: string; status: 'complete'; url: string; expiresAt: string }
  | { exportId: string; status: 'failed'; error?: string };

export interface PaidMediaColumnDef {
  key: string;
  label: string;
  format: 'string' | 'integer' | 'decimal';
}

export interface PaidMediaQueryResponse {
  columns: PaidMediaColumnDef[];
  data: Array<Record<string, unknown>>;
  filterOptions?: Record<string, string[]>;
  displayOptionValues?: Record<string, string[]>;
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export const {
  useAppControllerGetBaseQuery,
  useAppControllerGetMeQuery,
  useDistinctValuesControllerGetDistinctValuesQuery,
  useEventsControllerFindManyQuery,
  useProgramsControllerCreateMutation,
  useProgramsControllerFindManyQuery,
  useProgramsControllerGetQuery,
  useProgramsControllerUpdateMutation,
  useProgramsControllerDeleteMutation,
  useAssetsControllerFindManyQuery,
  useAssetsControllerUpdateMutation,
  useCampaignsControllerCreateMutation,
  useCampaignsControllerFindManyQuery,
  useCampaignsControllerFindManyDesiredQuery,
  useCampaignsControllerGetQuery,
  useCampaignsControllerUpdateMutation,
  useCampaignsControllerDeleteMutation,
  useCampaignsControllerGetPublishedQuery,
  useCampaignsControllerUpdatePublishedMutation,
  useCampaignsControllerValidateNotificationRecipientsMutation,
  useCampaignTitlesControllerGetCampaignTitlesQuery,
  useCampaignTitlesControllerGetCampaignTitlesBatchMutation,
  useCatalogControllerGetCatalogTitlesMutation,
  useSonataApiControllerSearchPageIdsQuery,
  useWorkflowsControllerCreateMutation,
  useWorkflowsControllerFindManyQuery,
  useWorkflowsControllerFindOneQuery,
  useWorkflowsControllerUpdateMutation,
  useWorkflowsControllerDeleteMutation,
  useQuicksightControllerGenerateTiktokVtaDashboardEmbedUrlMutation,
  useCampaignConfigurationControllerCreateMutation,
  useCampaignConfigurationControllerFindManyQuery,
  useCampaignConfigurationControllerGetQuery,
  useCampaignConfigurationControllerUpdateMutation,
  useCampaignConfigurationControllerDeleteMutation,
  useAgentSessionsControllerFindOneQuery,
  useAgentSessionsControllerUpdateMutation,
  useAgentSessionsControllerRemoveMutation,
  useAssetsControllerGetDistinctCampaignOptionsQuery,
  useGetDistinctTitlesQuery,
  useGetAllPublisherAccountConfigsQuery,
  useLazyValidateAliasQuery,
  useGetFeatureFlagsQuery,
  useGetSocialSignalsRecommendationsQuery,
  useQueryEntityMetricsQuery,
  useWorkflowExecutionPlanControllerFindManyQuery,
  useWorkflowExecutionPlanControllerFindOneQuery,
  useCampaignPlansControllerFindByCampaignIdQuery,
  useCampaignPlansControllerFindOneQuery,
  useCampaignPlansControllerUpdateStatusMutation,
  usePublisherEntitiesControllerListForCampaignQuery,
  useQueryPaidMediaQuery,
  useQueryPaidMediaInsightsQuery,
  useStartPaidMediaExportMutation,
  useLazyGetPaidMediaExportStatusQuery,
  useBrandControllerGetAvailablePresetsQuery,
  usePlatformAdminControllerCreateBusinessUnitMutation,
  usePlatformAdminControllerFindBusinessUnitsQuery,
  usePlatformAdminControllerUpdateBusinessUnitMutation,
  usePlatformAdminControllerDeleteBusinessUnitMutation,
  useBusinessUnitAdminControllerFindGroupsQuery,
  useBusinessUnitAdminControllerCreateGroupMutation,
  useBusinessUnitAdminControllerUpdateGroupMutation,
  useBusinessUnitAdminControllerUpdateGroupStatusMutation,
  useBusinessUnitAdminControllerFindPartnersQuery,
  useBusinessUnitAdminControllerFindBusinessUnitMembersQuery,
  useBusinessUnitAdminControllerCreatePartnerMutation,
  useBusinessUnitAdminControllerUpdatePartnerMutation,
  useBusinessUnitAdminControllerFindMembersQuery,
  useBusinessUnitAdminControllerAddMembershipMutation,
  useBusinessUnitAdminControllerBulkAddMembershipsMutation,
  useBusinessUnitAdminControllerUpdateMembershipMutation,
  useBusinessUnitAdminControllerRevokeMembershipMutation,
  useMyPermissionsControllerGetMyPermissionsQuery,
  useDocsControllerListPagesQuery,
  useDocsControllerSearchQuery,
} = leelaApi;

type DocsPageQueryArg = Parameters<typeof leelaApi.useDocsControllerGetPageQuery>[0];
type DocsPageQueryOptions = Parameters<typeof leelaApi.useDocsControllerGetPageQuery>[1];

/**
 * Docs page hook that refetches on window focus by default: the page's image URLs are presigned
 * and expire, so returning to a stale tab must fetch fresh ones. RTK Query has no per-endpoint
 * `refetchOnFocus`, hence the hook wrapper.
 */
export function useDocsControllerGetPageQuery (
  arg: DocsPageQueryArg,
  options?: DocsPageQueryOptions,
): ReturnType<typeof leelaApi.useDocsControllerGetPageQuery> {
  return leelaApi.useDocsControllerGetPageQuery(arg, { refetchOnFocus: true, ...options });
}
